import { useEffect, useState } from 'react';

import { AppDomainContext } from 'contexts/appDomain';
import AuthenticatedRoutes from './AuthenticatedRoutes';
import { GREEN_ZONE_SESSION_KEY, PUNCHUP_SESSION_KEY } from './constants';
import { Loader } from 'features/common/animations/Loader';
import NotAuthenticatedRoutes from './NotAuthenticatedRoutes';
import { RootState } from 'store';
import { setGreenZoneEnabled, setIsPunchup } from 'store/slices/AuthSlice';
import { silentLogin } from 'services/auth';
import { useAppDispatch } from 'store/hooks';
import useLoading from 'hooks/useLoading';
import { useSelector } from 'react-redux';
import { useSetApp } from 'hooks/useSetApp';

export const Routes = (): JSX.Element => {
	const { isAuthenticated } = useSelector((state: RootState) => state.auth);
	const { appDomain } = useSetApp();

	const dispatch = useAppDispatch();
	const isLoading = useLoading();
	const url = new URL(window.location.href);

	const [isPunchupLoading, setIsPunchupLoading] = useState(false);

	// silentLogin calls callback() with no args on success and callback(undefined, error)
	// on failure — so the auth error arrives in the SECOND argument, not the first.
	const punchupLoginCb = async (_result?: unknown, error?: unknown) => {
		if (error) {
			dispatch(setIsPunchup(false));
			// Tear the green-zone gate down alongside the punchup flag so a stale
			// greenZone=true cannot survive a failed auth into the next hard-reload recovery.
			sessionStorage.removeItem(GREEN_ZONE_SESSION_KEY);
			dispatch(setGreenZoneEnabled(false));
			// Notify the parent frame so it can issue a fresh token rather than
			// showing a login screen inside the iframe.
			if (window.parent !== window) {
				window.parent.postMessage({ action: 'logout' }, process.env.REACT_APP_DOWNSTROKE_PORTAL_URL || '*');
			}
		} else {
			dispatch(setIsPunchup(true));
		}
		setIsPunchupLoading(false);
	};

	// TIC-308: announce v2 protocol capability to the host as early as the green-zone gate resolves true, for
	// every load (URL-param launch + hard-reload sessionStorage recovery), entry-point-independent. The host
	// flips the session to protocol=2 and disarms its 1s legacy-detection fallback, so a slow event load can't
	// get the green dashboard mislabelled legacy. Green-gated because tixologi's App.tsx — and thus the height
	// channel — is shared with the legacy view, which must stay v1. No-op when standalone (not embedded).
	// See docs/iframe-communication.md → "Protocol v2".
	const announceV2Protocol = () => {
		if (window.parent !== window) {
			window.parent.postMessage(
				{ action: 'protocol-hello', version: 2 },
				process.env.REACT_APP_DOWNSTROKE_PORTAL_URL || '*'
			);
		}
	};

	useEffect(() => {
		const queryParams = new URLSearchParams(url.search);

		const token = queryParams.get('token') || '';
		const partnerId = queryParams.get('partnerId') || '';
		const isPunchupParam = queryParams.get('isPunchup');
		const greenZoneParam = queryParams.get('greenZone');

		if (isPunchupParam === 'true' && token && partnerId) {
			// Persist the punchup flag so a hard reload at a SPA-navigated sub-path
			// (which won't have the original query params) still enters punchup mode.
			sessionStorage.setItem(PUNCHUP_SESSION_KEY, 'true');
			// Persist + set the in-app green-zone gate in the same synchronous pass,
			// before the loader clears, so the correct UI renders without a flash.
			const greenZoneEnabled = greenZoneParam === 'true';
			sessionStorage.setItem(GREEN_ZONE_SESSION_KEY, String(greenZoneEnabled));
			dispatch(setGreenZoneEnabled(greenZoneEnabled));
			if (greenZoneEnabled) announceV2Protocol();
			setIsPunchupLoading(true);
			localStorage.setItem('accessToken', token);
			dispatch(silentLogin(token, punchupLoginCb));
		} else if (isPunchupParam === 'false') {
			sessionStorage.removeItem(PUNCHUP_SESSION_KEY);
			sessionStorage.removeItem(GREEN_ZONE_SESSION_KEY);
			dispatch(setGreenZoneEnabled(false));
			dispatch(setIsPunchup(false));
		} else if (sessionStorage.getItem(PUNCHUP_SESSION_KEY) === 'true') {
			// Hard reload scenario: URL has no isPunchup param because the SPA
			// previously navigated to a sub-path. Re-authenticate with the stored
			// token so the punchup context is restored correctly. Restore the
			// green-zone gate from sessionStorage too — missing this would drop a
			// gated venue to the legacy UI on reload.
			const greenZoneRestored = sessionStorage.getItem(GREEN_ZONE_SESSION_KEY) === 'true';
			dispatch(setGreenZoneEnabled(greenZoneRestored));
			if (greenZoneRestored) announceV2Protocol();
			const storedToken = localStorage.getItem('accessToken') || '';
			if (storedToken) {
				setIsPunchupLoading(true);
				dispatch(silentLogin(storedToken, punchupLoginCb));
			}
		}
	}, [url.search]);

	if (appDomain === null || isPunchupLoading) return <Loader />;

	return (
		<AppDomainContext.Provider value={appDomain}>
			{!!isLoading && <Loader />}
			{isAuthenticated ? <AuthenticatedRoutes /> : <NotAuthenticatedRoutes />}
		</AppDomainContext.Provider>
	);
};
